import { Injectable } from '@angular/core';
import { CheckinBaseClass } from '../../components';
import { FunctionIneligibleReason } from '../../master/function-ineligible-reason';
import { LOAD_MASTERS } from '../../master/master';
import { ApiErrorsInner, PageType } from '@lib/interfaces';
import { ErrorType } from '@lib/interfaces/errors';
import { ApiErrorResponseService, AswMasterService, ErrorsHandlerService, PageLoadingService } from '@lib/services';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription, filter, map, withLatestFrom } from 'rxjs';
import {
  CheckinSearchResultState,
  resetCheckinSearchResult,
  selectCheckinSearchResultIsFailureStatus,
  selectCheckinSearchResultState,
  setCheckinSearchResultFromApi,
} from 'src/common/store';
import {
  DefaultService as Api,
  PostJourneysSearchJourneyRequest,
  PostJourneysSearchJourneyResponse,
} from 'src/sdk_checkin';

@Injectable()
export class CheckinSearchResultStoreService extends CheckinBaseClass {
  private _result$: Observable<CheckinSearchResultState>;
  private _result!: CheckinSearchResultState;
  get result() {
    return this._result;
  }

  // 機能不可事由テーブルを取得するためのObservable
  private functionIneligibleReason$: Observable<FunctionIneligibleReason>;

  /**
   * チェックイン検索APIエラー一覧
   * key=エラーコード
   * value=エラーメッセージID
   */
  private static readonly ApiErrorMessages: { [errorCode: string]: string } = {
    EBAZ000004: 'E0272',
    EBAZ000005: 'E0258',
    EBAZ000006: 'E0257',
    EBAZ000007: 'E0273',
    EBAZ000008: 'E0259',
    // NOTE EBAZ000009 は、APIのエラーレスポンスからメッセージIDを取得する
    EBAZ000010: 'E0276',
    EBAZ000398: 'E0274',
    EBAZ000399: 'E0798',
  };

  /** APIエラーSubscription */
  apiErrorResponseSubscription?: Subscription;

  constructor(
    private _store: Store<CheckinSearchResultState>,
    private _api: Api,
    private _apiErrorResponseService: ApiErrorResponseService,
    private _errorsHandlerService: ErrorsHandlerService,
    private _pageLoadingService: PageLoadingService,
    _aswMasterService: AswMasterService
  ) {
    super();
    this._result$ = this._store.pipe(
      select(selectCheckinSearchResultState),
      filter((data) => !!data)
    );
    this.subscribeService(this._result$, (data) => {
      this._result = data;
    });
    this.functionIneligibleReason$ = _aswMasterService
      .getAswMasterByKey$(LOAD_MASTERS.FUNCTION_INELIGIBLE_REASON.key)
      .pipe(filter((state) => !!state));
  }

  override destroy(): void {
    if (!!this.apiErrorResponseSubscription) {
      this.apiErrorResponseSubscription.unsubscribe();
      this.apiErrorResponseSubscription = undefined;
    }
  }

  /**
   * CheckinSearchResultStateStore のObservableを返却する
   * @returns CheckinSearchResultStateStore の全ての値を取得するObservable
   */
  public getResultState$() {
    return this._result$;
  }

  /**
   * API呼び出しが失敗したかのObservableを返却する
   * @returns isFailureの値を取得するObservable
   */
  private getIsFailure$() {
    return this._store.pipe(select(selectCheckinSearchResultIsFailureStatus));
  }

  /**
   * チェックイン検索が正常終了したら結果を取得するObservable
   */
  getResultStateIfSuccessful$() {
    return this._result$.pipe(filter((state) => !state.isFailure && !state.isPending && !!state.model));
  }

  /**
   * API呼び出し結果のObservableを受け取りStoreに格納する
   * @param call API呼び出し結果を取得するObservable
   */
  private setResult(call: Observable<PostJourneysSearchJourneyResponse>) {
    this._store.dispatch(setCheckinSearchResultFromApi({ call }));
  }

  /**
   * チェックイン検索のストアをクリアする
   */
  public clear() {
    this._store.dispatch(resetCheckinSearchResult());
  }

  /**
   * チェックイン検索APIの呼び出しと、呼び出し結果のStoreへの設定を行う
   */
  callApi(condition: PostJourneysSearchJourneyRequest) {
    this.setupApiErrorHandler();
    const call = this._api.journeysSearchJourneyPost(this.makeGetJourneyIdRequestBody(condition));
    this.setResult(call);
  }

  /**
   * チェックイン検索APIのエラーを監視して処理する。
   */
  private setupApiErrorHandler() {
    if (!!this.apiErrorResponseSubscription) {
      return;
    }
    this.apiErrorResponseSubscription = this.getIsFailure$()
      .pipe(
        filter((state) => !!state),
        withLatestFrom(this._apiErrorResponseService.getApiErrorResponse$(), this.functionIneligibleReason$),
        map(([_, error, functionIneligibleReason]) => {
          const apiError = error?.errors?.[0];
          if (apiError) {
            const matchErrorMsgId = this.getMessageId(apiError, functionIneligibleReason);
            if (matchErrorMsgId) {
              this._errorsHandlerService.setRetryableError(PageType.PAGE, {
                errorMsgId: matchErrorMsgId,
                apiErrorCode: apiError.code,
              });
            } else {
              this._errorsHandlerService.setNotRetryableError({
                errorType: ErrorType.SYSTEM,
                apiErrorCode: apiError.code,
              });
            }
          }
          // ローディング画面表示終了
          this._pageLoadingService.endLoading();
        })
      )
      .subscribe();
  }

  /**
   * エラーメッセージIDの取得
   * @returns
   */
  getErrorMessageId$() {
    const apiErrorResponse$ = this._apiErrorResponseService.getApiErrorResponse$().pipe(filter((state) => !!state));
    return this.getIsFailure$().pipe(
      withLatestFrom(apiErrorResponse$, this.functionIneligibleReason$),
      map(([_, error, functionIneligibleReason]) => {
        const apiError = error?.errors?.[0];
        return !!apiError ? this.getMessageId(apiError, functionIneligibleReason) : undefined;
      })
    );
  }

  private getMessageId(apiError: ApiErrorsInner, functionIneligibleReason: FunctionIneligibleReason) {
    if (apiError.code === 'EBAZ000009') {
      const source = apiError.source as {
        items: string[];
      };
      // ASW不可事由まで展開した機能不可事由テーブル
      const functionIneligibleReasonByCheckin = functionIneligibleReason['checkin']['dxapiAcceptanceEligibility'];
      // エラーとして返されたdxapiReasons
      const DxApiIneligibleReason = source.items[0];
      return functionIneligibleReasonByCheckin[DxApiIneligibleReason]?.[0]?.error_message_id;
    } else {
      return CheckinSearchResultStoreService.ApiErrorMessages[apiError.code];
    }
  }

  /**
   * JourneyIdを取得するためのRequest Bodyを作成する
   * @param checkinSearchCondition
   * @returns
   */
  private makeGetJourneyIdRequestBody(checkinSearchCondition: PostJourneysSearchJourneyRequest) {
    if (checkinSearchCondition.searchType === PostJourneysSearchJourneyRequest.SearchTypeEnum.Order) {
      return {
        searchType: checkinSearchCondition.searchType,
        orderId: checkinSearchCondition.orderId,
        firstName: checkinSearchCondition.firstName,
        lastName: checkinSearchCondition.lastName,
        transitionedFromServicing: checkinSearchCondition.transitionedFromServicing || false,
      };
    } else if (checkinSearchCondition.searchType === PostJourneysSearchJourneyRequest.SearchTypeEnum.ETicket) {
      return {
        searchType: checkinSearchCondition.searchType,
        eTicketNumber: checkinSearchCondition.eTicketNumber,
        firstName: checkinSearchCondition.firstName,
        lastName: checkinSearchCondition.lastName,
        transitionedFromServicing: checkinSearchCondition.transitionedFromServicing || false,
      };
    } else if (checkinSearchCondition.searchType === PostJourneysSearchJourneyRequest.SearchTypeEnum.AmcMember) {
      return {
        searchType: checkinSearchCondition.searchType,
        firstName: checkinSearchCondition.firstName,
        lastName: checkinSearchCondition.lastName,
        transitionedFromServicing: checkinSearchCondition.transitionedFromServicing || false,
      };
    } else {
      return {
        searchType: checkinSearchCondition.searchType,
        firstName: checkinSearchCondition.firstName,
        lastName: checkinSearchCondition.lastName,
        transitionedFromServicing: checkinSearchCondition.transitionedFromServicing || false,
      };
    }
  }
}
